import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { EntityModal } from './EntityModal';
import { SearchableListSelect } from './SearchableListSelect';
import { extractApiErrorMessage } from '../../utils/apiError';

export interface UsageRow {
  label: string;
  count: number;
}

export interface ReassignOption {
  id: string;
  label: string;
  subtitle?: string;
}

export interface ReassignDeleteModalProps {
  open: boolean;
  onClose: () => void;
  /** Entity type shown in the copy (e.g. "vendor", "catalog item"). */
  entityTypeLabel: string;
  /** Name of the entity being deleted. */
  entityName: string;
  /** Linked-record counts; undefined while loading. */
  usage: UsageRow[] | undefined;
  /** Replacement candidates. The deleted entity itself is always filtered out. */
  options: ReassignOption[];
  /** Id of the entity being deleted — excluded from the options list. */
  excludeId: string;
  /** Resolves on success (modal closes); rejection keeps it open and shows the error. */
  onConfirm: (reassignToId: string | null) => Promise<void>;
  deleting?: boolean;
}

/**
 * Generic delete-with-reassignment dialog: shows what still references the
 * entity, and when anything does, requires picking a replacement (never the
 * entity being deleted) before the delete is allowed.
 */
export function ReassignDeleteModal({
  open,
  onClose,
  entityTypeLabel,
  entityName,
  usage,
  options,
  excludeId,
  onConfirm,
  deleting = false,
}: ReassignDeleteModalProps) {
  const [reassignToId, setReassignToId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setReassignToId(null);
      setError('');
    }
  }, [open]);

  const selectableOptions = useMemo(
    () => options.filter((o) => o.id !== excludeId),
    [options, excludeId],
  );

  const usageLoading = usage === undefined;
  const usedRows = (usage ?? []).filter((row) => row.count > 0);
  const totalCount = usedRows.reduce((sum, row) => sum + row.count, 0);
  const needsReassignment = totalCount > 0;

  const handleConfirm = useCallback(async () => {
    setError('');
    try {
      await onConfirm(needsReassignment ? reassignToId : null);
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Failed to delete'));
    }
  }, [onConfirm, needsReassignment, reassignToId, onClose]);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={`Delete ${entityTypeLabel}`}
      size="sm"
      presentation="centered"
      closeOnBackdrop={false}
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={usageLoading || deleting || (needsReassignment && !reassignToId)}
            className="px-4 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </>
      }
    >
      <div className="flex items-start gap-3 mb-4">
        <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={18} className="text-red-600" />
        </div>
        <p className="text-sm text-gray-700">
          You are about to permanently delete the {entityTypeLabel}{' '}
          <span className="font-semibold">&ldquo;{entityName}&rdquo;</span>. This action cannot be
          undone.
        </p>
      </div>

      {error && (
        <div className="mb-3 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      {usageLoading ? (
        <div className="space-y-2" data-testid="usage-skeleton">
          {[1, 2].map((i) => (
            <div key={i} className="h-6 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : needsReassignment ? (
        <>
          <div className="mb-4 border border-amber-200 bg-amber-50 rounded-md p-3">
            <p className="text-sm font-medium text-amber-800 mb-2">
              This {entityTypeLabel} is still linked to:
            </p>
            <ul className="text-sm text-amber-700 space-y-0.5">
              {usedRows.map((row) => (
                <li key={row.label} className="flex justify-between">
                  <span>{row.label}</span>
                  <span className="font-medium">{row.count}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reassign linked records to *
            </label>
            <SearchableListSelect
              items={selectableOptions}
              value={reassignToId}
              onChange={(v) => setReassignToId(typeof v === 'string' && v ? v : null)}
              getValue={(o) => o.id}
              getLabel={(o) => o.label}
              getSubtitle={(o) => o.subtitle}
              placeholder={`Select another ${entityTypeLabel}...`}
              ariaLabel={`Reassign to ${entityTypeLabel}`}
              emptyMessage={`No other ${entityTypeLabel} available`}
            />
            <p className="mt-1 text-xs text-gray-500">
              All linked records will be moved to the selected {entityTypeLabel} before deletion.
            </p>
          </div>
        </>
      ) : (
        <p className="text-sm text-gray-500">
          Nothing else references this {entityTypeLabel} — it can be deleted safely.
        </p>
      )}
    </EntityModal>
  );
}
